import { addDoc, collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';

const normalizeRole = (role) => String(role || '').toLowerCase().replace(/[\s-]+/g, '_');
const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

async function currentProfile() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Authentication is required.');
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) throw new Error('User profile was not found.');
  return { uid, ...snap.data() };
}

async function requireSuperAdmin() {
  const profile = await currentProfile();
  if (!['super_admin', 'superadmin'].includes(normalizeRole(profile.role || profile.userRole)) || profile.isBlocked === true) {
    throw new Error('Only Super Admin can manage employee meetings.');
  }
  return profile.uid;
}

async function validateAttendees(ids = [], hostId = '') {
  const unique = [...new Set((Array.isArray(ids) ? ids : []).filter(Boolean).filter((id) => id !== hostId))];
  if (!unique.length) return [];
  const rows = await Promise.all(unique.map(async (id) => {
    const [employeeSnap, userSnap] = await Promise.all([
      getDoc(doc(db, 'platformEmployees', id)),
      getDoc(doc(db, 'users', id)),
    ]);
    if (!employeeSnap.exists() || !userSnap.exists()) throw new Error('One of the selected employees no longer exists. Refresh the employee list.');
    const employee = employeeSnap.data() || {};
    const user = userSnap.data() || {};
    if (employee.status === 'inactive' || employee.isActive === false || user.isActive === false || user.isBlocked === true || normalizeRole(user.role || user.userRole) !== 'platform_employee') {
      throw new Error(`${employee.displayName || user.displayName || user.email || 'Selected employee'} is not an active platform employee.`);
    }
    return id;
  }));
  return rows;
}


async function validateDepartment(departmentId) {
  if (!departmentId) return '';
  const snap = await getDoc(doc(db, 'platformDepartments', departmentId));
  if (!snap.exists() || snap.data()?.isActive === false) throw new Error('Selected department is no longer active.');
  return departmentId;
}

async function requireRoomAccess(meetingId) {
  const profile = await currentProfile();
  const meetingSnap = await getDoc(doc(db, 'employeeMeetings', meetingId));
  if (!meetingSnap.exists() || meetingSnap.data()?.isDeleted === true) throw new Error('Employee meeting was not found.');
  const meeting = { id: meetingSnap.id, ...meetingSnap.data() };
  const role = normalizeRole(profile.role || profile.userRole);
  if (['super_admin', 'superadmin'].includes(role) && profile.isBlocked !== true) return { profile, meeting, isSuperAdmin: true };
  if (role !== 'platform_employee' || profile.isBlocked === true || profile.isActive === false) throw new Error('This room is only for active TELCOFAIR platform employees.');
  if (meeting.status === 'completed' || meeting.status === 'cancelled') throw new Error('This employee meeting has ended.');
  if (meeting.status !== 'live') throw new Error('This employee meeting has not started yet.');
  const employeeSnap = await getDoc(doc(db, 'platformEmployees', profile.uid));
  if (!employeeSnap.exists() || employeeSnap.data()?.status === 'inactive' || employeeSnap.data()?.isActive === false) throw new Error('Your employee access is disabled.');
  const attendeeIds = Array.isArray(meeting.attendeeIds) ? meeting.attendeeIds : [];
  if (meeting.hostId !== profile.uid && !attendeeIds.includes(profile.uid)) throw new Error('You are not invited to this employee meeting.');
  return { profile, meeting, isSuperAdmin: false };
}

export async function listEmployeeMeetings() {
  await requireSuperAdmin();
  const snap = await getDocs(collection(db, 'employeeMeetings'));
  return snap.docs
    .map((row) => ({ id: row.id, ...row.data() }))
    .filter((row) => row.isDeleted !== true)
    .sort((a, b) => toMillis(b.startAt || b.createdAt) - toMillis(a.startAt || a.createdAt));
}

export async function createEmployeeMeeting(data) {
  const hostId = await requireSuperAdmin();
  const ref = doc(collection(db, 'employeeMeetings'));
  const joinCode = Math.random().toString(36).slice(2, 8).toUpperCase();
  const attendeeIds = await validateAttendees(data.attendeeIds, hostId);
  const payload = {
    title: String(data.title || '').trim(),
    agenda: String(data.agenda || '').trim(),
    startAt: data.startAt || '',
    duration: Math.max(5, Number(data.duration || 30)),
    hostId,
    attendeeIds,
    departmentId: await validateDepartment(data.departmentId || ''),
    recurring: Boolean(data.recurring),
    reminderMinutes: Math.max(0, Number(data.reminderMinutes || 15)),
    status: 'scheduled',
    joinCode,
    isDeleted: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (!payload.title) throw new Error('Meeting title is required.');
  await setDoc(ref, payload);
  return { id: ref.id, ...payload };
}

export async function updateEmployeeMeeting(id, data) {
  const uid = await requireSuperAdmin();
  const current = await getDoc(doc(db, 'employeeMeetings', id));
  if (!current.exists() || current.data()?.isDeleted === true) throw new Error('Employee meeting was not found.');
  if (['live', 'completed', 'cancelled'].includes(current.data()?.status)) throw new Error('Only scheduled meetings can be edited.');
  const payload = {
    title: String(data.title ?? current.data().title ?? '').trim(),
    agenda: String(data.agenda ?? current.data().agenda ?? '').trim(),
    startAt: data.startAt ?? current.data().startAt ?? '',
    duration: Math.max(5, Number(data.duration ?? current.data().duration ?? 30)),
    attendeeIds: await validateAttendees(Array.isArray(data.attendeeIds) ? data.attendeeIds : current.data().attendeeIds || [], current.data().hostId),
    departmentId: await validateDepartment(data.departmentId ?? current.data().departmentId ?? ''),
    recurring: Boolean(data.recurring ?? current.data().recurring),
    reminderMinutes: Math.max(0, Number(data.reminderMinutes ?? current.data().reminderMinutes ?? 15)),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  };
  if (!payload.title) throw new Error('Meeting title is required.');
  await setDoc(doc(db, 'employeeMeetings', id), payload, { merge: true });
  return { success: true };
}

export async function startEmployeeMeeting(id) {
  const uid = await requireSuperAdmin();
  const snap = await getDoc(doc(db, 'employeeMeetings', id));
  if (!snap.exists() || snap.data()?.isDeleted === true) throw new Error('Employee meeting was not found.');
  if (snap.data()?.status === 'live') return { success: true };
  if (['completed', 'cancelled'].includes(snap.data()?.status)) throw new Error('This meeting cannot be restarted. Create a new meeting instead.');
  await setDoc(doc(db, 'employeeMeetings', id), { status: 'live', startedAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: uid }, { merge: true });
  return { success: true };
}

export async function completeEmployeeMeeting(id) {
  const { profile, meeting, isSuperAdmin } = await requireRoomAccess(id);
  if (meeting.status !== 'live') throw new Error('Only a live meeting can be ended.');
  if (!isSuperAdmin && meeting.hostId !== profile.uid) throw new Error('Only the meeting host can end this meeting.');
  await setDoc(doc(db, 'employeeMeetings', id), { status: 'completed', endedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
  return { success: true };
}

export async function deleteEmployeeMeeting(id) {
  const uid = await requireSuperAdmin();
  const snap = await getDoc(doc(db, 'employeeMeetings', id));
  if (!snap.exists()) return { success: true };
  if (snap.data()?.status === 'live') throw new Error('End the live meeting before removing it from the schedule.');
  await setDoc(doc(db, 'employeeMeetings', id), { isDeleted: true, status: snap.data()?.status === 'completed' ? 'completed' : 'cancelled', deletedAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: uid }, { merge: true });
  return { success: true };
}

export async function getEmployeeMeeting(id) {
  const { meeting } = await requireRoomAccess(id);
  if (meeting.status !== 'live') throw new Error('This employee meeting has not started yet.');
  return meeting;
}

export async function addMeetingChat(meetingId, message) {
  const text = String(message || '').trim();
  if (!text) throw new Error('Message cannot be empty.');
  const { profile, meeting } = await requireRoomAccess(meetingId);
  if (meeting.status !== 'live') throw new Error('Chat is only available while the meeting is live.');
  return addDoc(collection(db, 'employeeMeetings', meetingId, 'chat'), {
    uid: profile.uid,
    name: profile.displayName || profile.email || 'Employee',
    message: text,
    createdAt: serverTimestamp(),
  });
}

export default {
  listEmployeeMeetings,
  createEmployeeMeeting,
  updateEmployeeMeeting,
  startEmployeeMeeting,
  completeEmployeeMeeting,
  deleteEmployeeMeeting,
  getEmployeeMeeting,
  addMeetingChat,
};
